import { useState, useEffect } from "react";
import {
  BulkUploadWrapper,
  DragDropContainer,
  FontGrayNormal,
  StyledContainedButton,
} from "./style";
import {
  CircularProgress,
  Grid,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { CloudUploadIcon, ImageIcon } from "../../assets/Icons";
import { useTheme } from "styled-components";
import { useDropzone } from "react-dropzone";
import SelectBoardDropdown from "../commonComponents/selectBoardsDropdown";
import { useDispatch, useSelector } from "react-redux";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  openSaveNewAdLoaderSelector,
  boardItemsSelector,
} from "../../store/metadata/selector";
import { ToastTypes, notify } from "../../utils/helpers";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { BoardItem } from "../../store/metadata/types";
import { Delete as DeleteIcon } from "@mui/icons-material";
import DeleteConfirmationModal from "./deleteConfirmationModal";

interface UploadedFileData {
  file: File;
  id: string;
  selectedBoardIds: string[];
  selectedBoardsData: BoardItem[];
}

const BulkUploadModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileData[]>([]);
  const [pendingDeleteFileId, setPendingDeleteFileId] = useState<string | null>(null);
  const [confirmClearAllOpen, setConfirmClearAllOpen] = useState(false);
  const loading = useSelector(openSaveNewAdLoaderSelector);
  const boardItems = useSelector(boardItemsSelector);

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const validFileTypes = ["video/mp4", "image/jpeg", "image/png"];
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      acceptedFiles.forEach((file) => {
        if (validFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        notify({
          message: `Invalid file format(s): ${invalidFiles.join(
            ", "
          )}. Only mp4, jpeg or png format media is allowed`,
          type: ToastTypes.ERROR,
        });
      }

      if (validFiles.length > 0) {
        const newUploadedFiles = validFiles.map((file) => ({
          file,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          selectedBoardIds: [],
          selectedBoardsData: [],
        }));

        setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true, // Enable multiple file selection
  });
const handleSubmit = async () => {
  if (uploadedFiles.length === 0) {
    notify({
      message: "Please upload at least one file",
      type: ToastTypes.ERROR,
    });
    return;
  }
  const filesWithoutBoards = uploadedFiles.filter(
    (fileData) => fileData.selectedBoardIds.length === 0
  );

  if (filesWithoutBoards.length > 0) {
    notify({
      message: "Please select at least one board for each file",
      type: ToastTypes.ERROR,
    });
    return;
  }

  // Upload all files without showing individual toast
  const uploadPromises = uploadedFiles.map((fileData) => {
    const formData = new FormData();
    fileData.selectedBoardIds.forEach((boardId, index) => {
      formData.append(`boardIds[${index}]`, boardId);
    });
    formData.append("file", fileData.file);

    return dispatch(
      MetaDataSagaActions.manualUploadAd({
        body: formData,
        showSuccess: false, // disable toast per file
      })
    );
  });

  // Wait for all uploads to complete
  await Promise.all(uploadPromises);

  // Close modal and loader first
  dispatch(MetaDataReducerAction.setOpenSaveNewAd(false));
  dispatch(MetaDataReducerAction.setOpenSaveAdLoader(false));

  // Then show single success message
  notify({
    message: `${uploadedFiles.length} file(s) uploaded successfully!`,
    type: ToastTypes.SUCCESS,
  });

  // Clear uploaded files if needed
  setUploadedFiles([]);
};
  const handleUpdateBoard = (
    selectedBoardIds: string[],
    removedBoardId: string[],
    fileId: string
  ) => {
    setUploadedFiles((prev) =>
      prev.map((fileData) => {
        if (fileData.id === fileId) {
          // Update the selectedBoardsData when boards are selected
          let selectedBoards: BoardItem[] = [];
          if (selectedBoardIds.length > 0 && boardItems) {
            selectedBoards = boardItems.filter((board: BoardItem) =>
              selectedBoardIds.includes(board?._id)
            );
          }

          return {
            ...fileData,
            selectedBoardIds,
            selectedBoardsData: selectedBoards,
          };
        }
        return fileData;
      })
    );

    dispatch(MetaDataReducerAction.setOpenBoardDropdown("false"));
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prev) =>
      prev.filter((fileData) => fileData.id !== fileId)
    );
  };

  const handleClearAll = () => {
    setUploadedFiles([]);
  };

  // Load board data on component mount
  useEffect(() => {
    dispatch(MetaDataSagaActions.fetchBoardList({ search: "" }));
  }, [dispatch]);

  return (
    <BulkUploadWrapper>
      <Typography fontSize="1rem" fontWeight="500" color={theme.colors.gray1}>
        Use this function to upload multiple content files to Swipe Lounge for
        use in Boards.
      </Typography>

      <DragDropContainer {...getRootProps()}>
        <input {...getInputProps()} />
        <CloudUploadIcon />
        <Typography
          fontSize="1.125rem"
          fontWeight="600"
          color={theme.colors.black2}
          margin="1.25rem 0 0.625rem"
        >
          Upload Content
        </Typography>
        <FontGrayNormal>
          Drag and drop multiple .mp4, .png or .jpeg files, or click to select
        </FontGrayNormal>
      </DragDropContainer>

      {uploadedFiles.length > 0 && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography
              fontSize="1rem"
              fontWeight="600"
              color={theme.colors.black2}
            >
              Files to Upload ({uploadedFiles.length})
            </Typography>
            <StyledContainedButton
              variant="outlined"
              size="small"
              onClick={() => setConfirmClearAllOpen(true)}
              sx={{ padding: "0.25rem 1rem" }}
            >
              Clear All
            </StyledContainedButton>
          </Stack>

          <Grid container spacing={2.5} sx={{ marginBottom: "1.875rem" }}>
            <Grid item sm={5}>
              <FontGrayNormal>Asset</FontGrayNormal>
            </Grid>
            <Grid item sm={6}>
              <FontGrayNormal>Add to Board</FontGrayNormal>
            </Grid>
            <Grid item sm={1}>
              <FontGrayNormal>Action</FontGrayNormal>
            </Grid>

            {uploadedFiles.map((fileData) => (
              <Grid container item spacing={2.5} key={fileData.id}>
                <Grid item sm={5}>
                  <Stack direction="row" gap="0.625rem" alignItems="center">
                    <ImageIcon />
                    <Typography
                      fontSize="1rem"
                      fontWeight="500"
                      color={theme.colors.black2}
                      sx={{
                        overflowWrap: "break-word",
                      }}
                      maxWidth="15rem"
                    >
                      {fileData.file.name}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item sm={6}>
                  <SelectBoardDropdown
                    showSelectedItemCount={2}
                    selectedBoards={fileData.selectedBoardsData}
                    handleUpdateBoard={(selectedBoardIds, removedBoardId) =>
                      handleUpdateBoard(
                        selectedBoardIds,
                        removedBoardId,
                        fileData.id
                      )
                    }
                  />
                </Grid>
                <Grid item sm={1}>
                  <IconButton
                    onClick={() => setPendingDeleteFileId(fileData.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <StyledContainedButton
        variant="contained"
        size="small"
        sx={{
          float: "right",
          padding: "0.313rem 2.688rem",
        }}
        onClick={handleSubmit}
        disabled={loading || uploadedFiles.length === 0}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
      >
        Upload All ({uploadedFiles.length})
      </StyledContainedButton>

      <DeleteConfirmationModal
        isOpen={!!pendingDeleteFileId}
        handleClose={() => setPendingDeleteFileId(null)}
        handleDelete={() => {
          if (pendingDeleteFileId) {
            handleRemoveFile(pendingDeleteFileId);
            setPendingDeleteFileId(null);
          }
        }}
        modalTitle="Remove file?"
        modalDescription="This file will be removed from the upload list."
        btnLabel="Remove"
      />

      <DeleteConfirmationModal
        isOpen={confirmClearAllOpen}
        handleClose={() => setConfirmClearAllOpen(false)}
        handleDelete={() => {
          handleClearAll();
          setConfirmClearAllOpen(false);
        }}
        modalTitle="Clear all files?"
        modalDescription="All files will be removed from the upload list."
        btnLabel="Clear All"
      />
    </BulkUploadWrapper>
  );
};

export default BulkUploadModal;
